import * as React from 'react';
import ReactDOM from 'react-dom/client';

import type {ToastType} from 'common/components/toast/types';
import {LocaleSafeString, StrictTranslatable} from 'common/i18n';
import {reporter} from 'common/report/error-reporting';

import {ToastAreaReact} from './toast-area-content';

export const DEFAULT_TOAST_TTL = 7000;

export interface Toast {
  id?: number;
  type?: ToastType;
  cta?: string;
  ctaHref?: string;
  ctaTarget?: string;
  ctaCallback?: () => void;
  closeToastCallback?: () => void;
  undoCallback?: () => void;
  elref?: string;
  hide?: boolean;
  hideCancelButton?: boolean;
  isError?: boolean;
  undoable?: boolean;
  undoLabel?: string;
  timeout?: number;
  isStatic?: boolean;
  persistent?: boolean;
  ttl?: number;
  message: StrictTranslatable | LocaleSafeString | React.ReactNode;
}

let nextId = 1;

export class ToastArea extends HTMLElement {
  private root: ReactDOM.Root | null = null;
  private toasts: Toast[] = [];

  connectedCallback(): void {
    if (this.root) {
      return;
    }
    this.root = ReactDOM.createRoot(this);
    this.renderTree();
  }

  disconnectedCallback(): void {
    const root = this.root;
    this.root = null;
    if (root) {
      window.setTimeout(() => root.unmount(), 0);
    }
  }

  public addToast(toast: Toast): Toast {
    const newToast: Toast = {
      id: nextId++,
      timeout: DEFAULT_TOAST_TTL,
      ...toast,
    };
    this.toasts = [...this.toasts, newToast];
    this.renderTree();
    return newToast;
  }

  public closeToast(toast: Toast): void {
    if (!toast) {
      reporter.warn(`Calling closeToast with null param`);
      return;
    }
    this.toasts = this.toasts.map((t) => (t.id === toast.id ? {...t, hide: true} : t));
    this.renderTree();
  }

  public clearAllToasts(): void {
    this.toasts = this.toasts.filter((t) => t.persistent);
    this.renderTree();
  }

  private renderTree(): void {
    if (!this.root) {
      return;
    }
    this.root.render(
      React.createElement(ToastAreaReact, {
        toasts: this.toasts,
        onAction: (t) => {
          t.ctaCallback?.();
          this.closeToast(t);
        },
        onClose: (t) => {
          t.closeToastCallback?.();
          this.closeToast(t);
        },
        onUndo: (t) => {
          t.undoCallback?.();
          this.closeToast(t);
        },
      }),
    );
  }
}

if (typeof window !== `undefined` && !window.customElements.get(`mp-toast-area`)) {
  window.customElements.define(`mp-toast-area`, ToastArea);
}
